// web/src/registry/features/_part-1.ts —— FEATURES 的第 1/6 段（20 个元素：chat / inbound / assist / knowledge / quality / workflow / outbound）
// 拆自 web/src/registry/features.ts（元素原样搬运、顺序不变）；由入口按顺序展开组合，故本片是**内部片**，不对外导出。
import type { Feature } from '../features';

export const FEATURES_PART_1: Feature[] = [
  // ───────────────────────────── 工作台（跨域聚合入口，只展示与操作，不拥有业务配置） ─────────────────────────────
  {
    id: 'chat.workspace',
    title: '在线会话',
    domain: 'chat',
    scope: 'domain',
    type: 'workspace',
    route: '/workbench',
    permission: null,
    priority: 'P2',
  },
  {
    id: 'inbound.softphone',
    title: '软电话',
    domain: 'inbound',
    scope: 'domain',
    type: 'workspace',
    route: '/workbench',
    permission: null,
    priority: 'P2',
  },
  {
    id: 'assist.workspace',
    title: '实时坐席辅助',
    domain: 'assist',
    scope: 'domain',
    type: 'workspace',
    route: '/workbench',
    permission: null,
    priority: 'P2',
  },
  {
    id: 'knowledge.workspace',
    title: '知识建议',
    domain: 'knowledge',
    scope: 'domain',
    type: 'workspace',
    route: '/workbench',
    permission: null,
    priority: 'P2',
  },
  {
    id: 'quality.appeals',
    title: '我的质检与申诉',
    domain: 'quality',
    scope: 'domain',
    type: 'workspace',
    route: '/quality/appeals',
    legacyLocations: ['/quality?tab=appeals'],
    permission: 'quality.reports',
    priority: 'P1',
    issue: '坐席入口原埋在质检管理 Tab 中；不占全局导航，经待办中心/工作台进入',
  },
  {
    id: 'workflow.inbox',
    title: '我的待办',
    domain: 'workflow',
    scope: 'platform',
    type: 'workspace',
    route: '/workbench/tasks',
    legacyLocations: ['/workflow'],
    permission: 'workflow.inbox',
    priority: 'P1',
    issue: '入口已提升为顶部一级菜单「待办中心」；引擎属平台。页内三页签：待办（流程任务）/ 通知 / 公告——后两者原为独立页「消息中心」，并入本页后与通知铃的三个页签一一对应',
  },

  // ───────────────────────────── 业务应用 ─────────────────────────────

  // 外呼
  {
    id: 'outbound.board',
    title: '号码看板',
    domain: 'outbound',
    scope: 'domain',
    type: 'workspace',
    route: '/outbound/board',
    legacyLocations: ['/outbound'],
    permission: 'outbound',
    priority: 'P2',
    issue: '号码看板为任务详情内组件（需 taskId），不作为独立侧栏入口',
  },
  {
    id: 'outbound.jobs',
    title: '外呼任务',
    domain: 'outbound',
    scope: 'domain',
    type: 'resource',
    route: '/outbound/jobs',
    legacyLocations: ['/outbound'],
    permission: 'outbound.jobs',
    nav: { section: 'business', group: 'outbound', order: 1 },
    priority: 'P1',
    issue: '任务与场景共用 /outbound',
  },
  {
    id: 'outbound.scenes',
    title: '外呼场景',
    domain: 'outbound',
    scope: 'domain',
    type: 'resource',
    route: '/outbound/scenes',
    legacyLocations: ['/outbound?tab=scenarios'],
    permission: 'outbound.scenes',
    nav: { section: 'business', group: 'outbound', order: 2 },
    priority: 'P1',
  },
  {
    id: 'outbound.scene.detail',
    title: '场景详情（话术/转人工/结果字段/AI 策略）',
    domain: 'outbound',
    scope: 'domain',
    type: 'config',
    route: '/outbound/scenes/:sceneId',
    permission: 'outbound',
    priority: 'P1',
  },
  {
    id: 'outbound.settings',
    title: '外呼设置',
    domain: 'outbound',
    scope: 'domain',
    type: 'config',
    route: '/outbound/settings',
    permission: 'outbound.settings',
    nav: { section: 'business', group: 'outbound', order: 3 },
    priority: 'P1',
    issue: '承载同时执行的外呼任务数（原混在通信设置）；全局 AI 并发已归「机器人 → 语音行为默认」',
  },
  {
    id: 'outbound.settings.concurrency',
    title: '外呼并发',
    domain: 'outbound',
    scope: 'domain',
    type: 'config',
    route: '/outbound/settings/concurrency',
    legacyLocations: ['/system/sys-settings'],
    permission: 'outbound.settings',
    priority: 'P0',
    issue: '业务配置误入 /system/sys-settings（Wrong Ownership）',
  },
  {
    id: 'outbound.analytics',
    title: '外呼分析',
    domain: 'outbound',
    scope: 'domain',
    type: 'analytics',
    route: '/outbound/analytics',
    orphan: true,
    legacyLocations: ['/report'],
    permission: 'outbound',
    priority: 'P1',
    issue: '已收敛到「运营分析 → 报表中心」，不再占业务域导航',
  },

  // 在线客服
  {
    id: 'chat.sessions',
    title: '会话记录',
    domain: 'chat',
    scope: 'domain',
    type: 'record',
    route: '/chat/sessions',
    legacyLocations: ['/chat'],
    permission: 'chat.sessions',
    nav: { section: 'business', group: 'chat', order: 1 },
    priority: 'P2',
  },
  {
    id: 'chat.quickreplies.personal',
    title: '快捷回复',
    domain: 'chat',
    scope: 'domain',
    type: 'resource',
    route: '/chat/quick-replies',
    legacyLocations: ['/chat/quick-replies'],
    permission: 'chat.quickreplies',
    nav: { section: 'business', group: 'chat', order: 2 },
    priority: 'P2',
  },
  {
    id: 'chat.settings',
    title: '在线客服设置',
    domain: 'chat',
    scope: 'domain',
    type: 'config',
    route: '/chat/settings',
    permission: 'chat.settings',
    nav: { section: 'business', group: 'chat', order: 3 },
    priority: 'P1',
    issue: '渠道接待参数（在线接待量/电话优先）；机器人配置本体归 bot.chat-robots，两者已拆分',
  },
  {
    id: 'chat.analytics',
    title: '在线客服分析',
    domain: 'chat',
    scope: 'domain',
    type: 'analytics',
    route: '/chat/analytics',
    permission: null,
    priority: 'P1',
    issue: '已收敛到「运营分析 → 报表中心」，不再占业务域导航',
  },

  // 呼入
  {
    id: 'inbound.calls',
    title: '呼叫记录',
    domain: 'inbound',
    scope: 'domain',
    type: 'record',
    route: '/inbound/calls',
    legacyLocations: ['/calls'],
    permission: 'inbound.calls',
    nav: { section: 'business', group: 'inbound', order: 1 },
    priority: 'P2',
  },
  {
    id: 'inbound.transcripts',
    title: '通话文本',
    domain: 'inbound',
    scope: 'domain',
    type: 'record',
    route: '/inbound/transcripts',
    legacyLocations: ['/calls/transcripts'],
    permission: 'inbound.transcripts',
    nav: { section: 'business', group: 'inbound', order: 2 },
    priority: 'P2',
  },
  {
    id: 'inbound.blacklist',
    title: '黑名单',
    domain: 'inbound',
    scope: 'domain',
    type: 'resource',
    route: '/inbound/blacklist',
    legacyLocations: ['/calls/blacklist', '/inbound/settings?tab=blacklist'],
    permission: 'inbound.blacklist',
    nav: { section: 'business', group: 'inbound', order: 3 },
    priority: 'P1',
    issue: '原为「呼入设置」页内默认 Tab，2026-09 拆解后独立成条（原来点进设置页只看得到它）；读写权限（坐席可写）待权限重构时收口',
  },];
